import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FaCalendarAlt } from 'react-icons/fa';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../services/api';
import '../css/financeiro-parcela-pagar.css';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);

const normalizarEvento = (evento) => ({
    ...evento,
    start: evento.start ? new Date(evento.start) : null,
    end: evento.end ? new Date(evento.end) : null,
});

export default function CalendarioFreelancers() {
    const [ocupacoes, setOcupacoes] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [calendarView, setCalendarView] = useState('month');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const calendarRef = useRef(null);
    const token = localStorage.getItem('token');
    const history = useHistory();

    const buscarOcupacoes = useCallback(async () => {
        try {
            setCarregando(true);
            const response = await api({
                method: 'get',
                action: '/eventoFreelancerParticipanteCalendario',
                token,
            });
            if (response.data.status === true) {
                setOcupacoes((response.data.data ?? []).map(normalizarEvento));
            }
        } catch (error) {
            console.error('Erro ao buscar ocupacoes dos freelancers:', error);
        } finally {
            setCarregando(false);
        }
    }, [token]);

    useEffect(() => {
        buscarOcupacoes();
    }, [buscarOcupacoes]);

    const handleEventClick = (event) => {
        if (!event.evento_id) {
            return;
        }
        let url = `/evento?idEvento=${event.evento_id}`;
        if (event.situacao === 'fechado') {
            url += '&situacao=fechado';
        }
        history.push(url);
    };

    const paginados = useMemo(() => {
        const inicio = (page - 1) * pageSize;
        return ocupacoes.slice(inicio, inicio + pageSize);
    }, [ocupacoes, page, pageSize]);

    const totalPaginas = Math.max(1, Math.ceil(ocupacoes.length / pageSize));

    return (
        <div className="fin-pp__card" style={{ padding: '16px', overflow: 'hidden' }}>
            <div className="fin-pp__cphead" style={{ marginBottom: 16 }}>
                <span className="fin-pp__cphead-ic"><FaCalendarAlt size={15} /></span>
                <span className="fin-pp__cphead-title">Contratações dos freelancers</span>
            </div>

            {carregando ? (
                <div className="fin-pp__empty" style={{ padding: '48px 0' }}>Carregando calendário...</div>
            ) : (
                <>
                    <div style={{ height: 500, marginBottom: 20 }}>
                        <div ref={calendarRef} />
                        <Calendar
                            localizer={localizer}
                            events={ocupacoes}
                            startAccessor="start"
                            endAccessor="end"
                            onSelectEvent={handleEventClick}
                            views={['month', 'week', 'day']}
                            date={calendarDate}
                            view={calendarView}
                            onNavigate={(date) => setCalendarDate(date)}
                            onView={(view) => setCalendarView(view)}
                            messages={{
                                next: 'Próximo',
                                previous: 'Anterior',
                                today: 'Hoje',
                                month: 'Mês',
                                week: 'Semana',
                                day: 'Dia',
                                noEventsInRange: 'Não há contratações neste período.',
                            }}
                            eventPropGetter={(event) => ({
                                style: {
                                    backgroundColor: event.backgroundColor,
                                    borderColor: event.borderColor,
                                    color: event.textColor,
                                },
                            })}
                            tooltipAccessor={(event) => [
                                event.title,
                                `Evento: ${event.evento ?? 'N/A'}`,
                                `Cliente: ${event.cliente ?? 'N/A'}`,
                                `Código: ${event.codigo ?? 'N/A'}`,
                                event.especialidade ? `Especialidade: ${event.especialidade}` : null,
                                event.descricao_servico ? `Serviço: ${event.descricao_servico}` : null,
                            ].filter(Boolean).join('\n')}
                        />
                    </div>

                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 980, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Freelancer</th>
                                    <th>Evento</th>
                                    <th>Cliente</th>
                                    <th>Especialidade</th>
                                    <th>Entrada</th>
                                    <th>Saída</th>
                                    <th>Serviço</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginados.length === 0 ? (
                                    <tr><td colSpan={7} className="fin-pp__empty">Nenhum registro</td></tr>
                                ) : paginados.map((row, index) => (
                                    <tr
                                        key={`${row.evento_id ?? 'evento'}-${row.title ?? 'freela'}-${index}`}
                                        onClick={() => {
                                            if (row.start) {
                                                setCalendarDate(row.start);
                                                calendarRef.current?.scrollIntoView({ behavior: 'smooth' });
                                            }
                                        }}
                                        style={{ cursor: row.start ? 'pointer' : 'default' }}
                                    >
                                        <td className="fin-pp__cell-strong">{row.title ?? ''}</td>
                                        <td className="fin-pp__cell-muted">{row.evento ?? ''}</td>
                                        <td className="fin-pp__cell-muted">{row.cliente ?? ''}</td>
                                        <td className="fin-pp__cell-muted">{row.especialidade ?? ''}</td>
                                        <td className="fin-pp__cell-muted">{row.start ? moment(row.start).format(row.allDay ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm') : ''}</td>
                                        <td className="fin-pp__cell-muted">{row.end ? moment(row.end).format(row.allDay ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm') : ''}</td>
                                        <td className="fin-pp__cell-muted">{row.descricao_servico ?? ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{ocupacoes.length} registros</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select
                                className="fin-pp__select"
                                style={{ height: 34 }}
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage((state) => state - 1)}>
                                Anterior
                            </button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage((state) => state + 1)}>
                                Próxima
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
